import type {
  CoachingArtifactDto,
  CoachingArtifactVisibility,
  CoachingCohortDto,
  CoachingCompanyDto,
  CoachingDeliveryMode,
  CoachingHistoryEventDto,
  CoachingOperationResult,
  CoachingOrgAnalyticsSummary,
  CoachingOrgAnalyticsTimeSeries,
  CoachingOrgReportStatus,
  CoachingParticipantDto,
  CoachingProgramDto,
  CoachingReportType,
  CoachingResourceDto,
  CoachingSessionDto,
  CoachingUserDto,
  ListResponse
} from "./coaching-dtos";

/**
 * Live Coaching API read-model adapters.
 *
 * The live backend wraps every payload in a contract envelope
 * (`{ contract_version, <family>: ..., <family>Evidence, sideEffects }`) and
 * uses camelCase domain field names (`cohortName`, `coachUserId`, ...). Mock
 * services already answer in frontend DTO shape, so every adapter passes
 * DTO-shaped payloads through unchanged.
 */

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function asString(value: unknown): string | undefined {
  if (typeof value === "string" && value.trim()) return value;
  if (typeof value === "number") return String(value);
  return undefined;
}

const envelopeMetaKeys = new Set(["contract_version", "sideEffects", "profileEvidence"]);

function unwrapEnvelopeRecord(payload: unknown): UnknownRecord | null {
  if (!isRecord(payload)) return null;
  if (asString(payload.id)) return payload;
  for (const [key, value] of Object.entries(payload)) {
    if (envelopeMetaKeys.has(key) || key.endsWith("Evidence")) continue;
    if (isRecord(value) && asString(value.id)) return value;
  }
  return null;
}

function unwrapEnvelopeRows(payload: unknown): unknown[] | null {
  if (!isRecord(payload)) return null;
  for (const [key, value] of Object.entries(payload)) {
    if (envelopeMetaKeys.has(key) || key.endsWith("Evidence")) continue;
    if (Array.isArray(value)) return value;
  }
  return null;
}

export type LiveRowMapper<T> = (row: UnknownRecord) => T;

function mapRow<T>(row: UnknownRecord, mapper: LiveRowMapper<T>): T {
  return mapper(row);
}

export function toLiveListResponse<T>(payload: unknown, mapper: LiveRowMapper<T>): ListResponse<T> {
  if (isRecord(payload) && Array.isArray(payload.items)) return payload as unknown as ListResponse<T>;
  const root = isRecord(payload) ? payload : null;
  const participantRows = root?.participants;
  const sessionRows = root?.sessions;
  const rows: unknown[] = Array.isArray(participantRows)
    ? participantRows
    : Array.isArray(sessionRows)
      ? sessionRows
      : unwrapEnvelopeRows(payload) ?? [];
  return {
    items: rows.filter(isRecord).map((row) => mapRow(row, mapper)),
    next_cursor: root?.next_cursor == null ? null : String(root.next_cursor),
    ...(typeof root?.page === "number" ? { page: root.page } : {}),
    ...(typeof root?.pageSize === "number" ? { pageSize: root.pageSize } : {}),
    ...(typeof root?.total === "number" ? { total: root.total } : {}),
    ...(typeof root?.hasNext === "boolean" ? { hasNext: root.hasNext } : {})
  };
}

export function toLiveRecordResponse<T>(payload: unknown, mapper: LiveRowMapper<T>): T {
  // Always run the row mapper: live envelopes and bare camelCase records both need
  // the dual-key projection (session-report/detail reads are single-record paths).
  if (isRecord(payload) && asString(payload.id) && !("contract_version" in payload)) {
    return mapRow(payload, mapper);
  }
  const record = unwrapEnvelopeRecord(payload);
  const mapped = record ? mapRow(record, mapper) : (payload as T);
  if (record && isRecord(payload) && isRecord(mapped)) {
    const mutationMetadata = {
      ...(Object.prototype.hasOwnProperty.call(payload, "outcome") ? { outcome: payload.outcome } : {}),
      ...(Object.prototype.hasOwnProperty.call(payload, "replayed") ? { replayed: payload.replayed } : {})
    };
    if (Object.keys(mutationMetadata).length > 0) return { ...mapped, ...mutationMetadata } as T;
  }
  return mapped;
}

export function toLiveOperationResult(payload: unknown): CoachingOperationResult {
  if (isRecord(payload) && typeof payload.ok === "boolean") return payload as unknown as CoachingOperationResult;
  const record = unwrapEnvelopeRecord(payload);
  const root = isRecord(payload) ? payload : undefined;
  // Series preview (live shape): carry the DST confirmation token — create
  // REQUIRES echoing it — and map the previewed rows for the review table
  // (2026-07-05 walk finding: dropping these made live series creation 400
  // and rendered an empty preview table).
  const previewConfirmation =
    isRecord(root?.previewConfirmation) ? (root?.previewConfirmation as { token?: string; required?: boolean; reason?: string }) : undefined;
  const previewRows = Array.isArray(root?.preview) ? (root?.preview as Array<Record<string, unknown>>) : undefined;
  const seriesSessionsRows = Array.isArray(root?.sessions) ? (root?.sessions as Array<Record<string, unknown>>) : undefined;
  const cohortEnvelope = isRecord(root?.cohort) ? (root.cohort as Record<string, unknown>) : undefined;
  // Session mutations (reschedule/lifecycle) return the updated row — preserve
  // it for write-truth cache patching (class 12).
  const sessionRecord = isRecord(root?.session) ? (root?.session as Record<string, unknown>) : undefined;
  const reviewRecord = isRecord(root?.review) ? (root.review as Record<string, unknown>) : undefined;
  // Participant mutations (remove/update) return the mutated participant row —
  // preserve it so hooks can patch/drop list rows instead of stale-refetching.
  const participantRecord = isRecord(root?.participant) ? (root.participant as Record<string, unknown>) : undefined;
  const participantsRows = Array.isArray(root?.participants) ? (root.participants as Array<Record<string, unknown>>) : undefined;
  const bulkEnvelope = isRecord(root?.bulk) ? root.bulk : undefined;
  const bulkRows = Array.isArray(bulkEnvelope?.rowResults)
    ? bulkEnvelope.rowResults as Array<Record<string, unknown>>
    : undefined;
  const bulkRowResults = bulkRows ?? (Array.isArray(root?.rowResults) ? root.rowResults as Array<Record<string, unknown>> : undefined);
  const importedCount = typeof bulkEnvelope?.importedCount === "number"
    ? bulkEnvelope.importedCount
    : typeof root?.importedCount === "number" ? root.importedCount : undefined;
  const failedCount = typeof bulkEnvelope?.failedCount === "number"
    ? bulkEnvelope.failedCount
    : typeof root?.failedCount === "number" ? root.failedCount : undefined;
  const seriesRowResults =
    seriesSessionsRows && seriesSessionsRows.length
      ? seriesSessionsRows.map((row, index) => ({
          ...row,
          id: String(row.id ?? index + 1),
          cohort_id: asString(row.cohort_id ?? row.cohortId) ?? asString(cohortEnvelope?.cohortId),
          title: asString(row.title) ?? `Session ${index + 1}`,
          starts_at: String(
            row.starts_at ??
              (row.sessionDate && row.startTime ? `${row.sessionDate}T${row.startTime}` : (row.sessionDate ?? ""))
          ),
          timezone: asString(row.timezone),
          status: asString(row.status) ?? "scheduled"
        }))
      : undefined;
  const rowResultsFromParticipants =
    participantsRows && participantsRows.length
      ? participantsRows.map((row, index) => ({
          ...row,
          id: String(row.id ?? index + 1),
          status: asString(row.status) ?? "imported"
        }))
      : undefined;
  const rowResultsFromPreview =
    previewRows && previewRows.length
      ? previewRows.map((row, index) => ({
          ...row,
          id: String(row.id ?? row.sequence ?? index + 1),
          title: String(row.title ?? `Session ${index + 1}`),
          starts_at: String(
            row.starts_at ?? (row.scheduledDate && row.startTime ? `${row.scheduledDate}T${row.startTime}` : "")
          )
        }))
      : undefined;
  const row_results = rowResultsFromParticipants ?? rowResultsFromPreview ?? seriesRowResults;
  // Invite send/force-resend (live): keep the API's actual operation and invite-lane state so the UI
  // reports what the server recorded (provider mode, operation status) instead of static endpoint metadata.
  const operationRecord = isRecord(root?.operation) ? (root.operation as Record<string, unknown>) : undefined;
  const inviteLaneRecord = isRecord(root?.inviteLane) ? (root.inviteLane as Record<string, unknown>) : undefined;
  const operationStateRecord = isRecord(root?.operationState) ? (root.operationState as Record<string, unknown>) : undefined;
  return {
    // requestContract only resolves on HTTP success, so a live envelope means the operation landed.
    ok: true,
    id: asString(record?.id) ?? asString(cohortEnvelope?.cohortId) ?? asString(root?.id),
    status:
      asString(record?.status) ??
      asString(isRecord(root?.series) ? (root.series as Record<string, unknown>).status : undefined) ??
      asString(operationRecord?.status) ??
      asString(root?.status),
    reference: asString(root?.reference),
    ...(operationRecord ? { operation: operationRecord } : {}),
    ...(inviteLaneRecord ? { inviteLane: inviteLaneRecord } : {}),
    ...(operationStateRecord ? { operationState: operationStateRecord } : {}),
    ...(previewConfirmation ? { previewConfirmation } : {}),
    ...(sessionRecord ? { session: sessionRecord } : {}),
    ...(reviewRecord ? { review: reviewRecord } : {}),
    ...(participantRecord ? { participant: participantRecord } : {}),
    ...(bulkRowResults || importedCount !== undefined || failedCount !== undefined ? {
      imported_count: importedCount,
      failed_count: failedCount,
      row_results: bulkRowResults
    } : {}),
    ...(row_results ? { row_results } : {})
  };
}

export const liveCompanyRow: LiveRowMapper<CoachingCompanyDto> = (row) => ({
  ...(row as Partial<CoachingCompanyDto>),
  id: asString(row.id) ?? "",
  name: asString(row.name) ?? "",
  status: asString(row.status),
  org_id: asString(row.org_id ?? row.orgId)
});

export const liveProgramRow: LiveRowMapper<CoachingProgramDto> = (row) => ({
  ...(row as Partial<CoachingProgramDto>),
  id: asString(row.id) ?? "",
  name: asString(row.name ?? row.programName) ?? "",
  company_id: asString(row.company_id ?? row.companyId),
  company_name: asString(row.company_name ?? row.companyName),
  coach_id: asString(row.coach_id ?? row.coachUserId),
  coach_name: asString(row.coach_name ?? row.coachName),
  status: asString(row.status)
});

export const liveCohortRow: LiveRowMapper<CoachingCohortDto> = (row) => ({
  ...(row as Partial<CoachingCohortDto>),
  id: asString(row.id) ?? "",
  name: asString(row.name ?? row.cohortName) ?? "",
  company_id: asString(row.company_id ?? row.companyId),
  company_name: asString(row.company_name ?? row.companyName),
  program_id: asString(row.program_id ?? row.programId),
  program_name: asString(row.program_name ?? row.programName),
  coach_id: asString(row.coach_id ?? row.coachUserId),
  coach_name: asString(row.coach_name ?? row.coachName),
  // Backend read mapper emits camelCase `startDate` (date-only YYYY-MM-DD);
  // surface it as start_date for the Wave-5 coach cutoff.
  start_date: asString(row.start_date ?? row.startDate),
  status: asString(row.status)
});

export const liveParticipantRow: LiveRowMapper<CoachingParticipantDto> = (row) => ({
  ...(row as Partial<CoachingParticipantDto>),
  id: asString(row.id) ?? "",
  email: asString(row.email),
  name: asString(row.name),
  cohort_id: asString(row.cohort_id ?? row.cohortId),
  cohort_name: asString(row.cohort_name ?? row.cohortName),
  company_name: asString(row.company_name ?? row.companyName),
  status: asString(row.status)
});

export const liveSessionRow: LiveRowMapper<CoachingSessionDto> = (row) => {
  const sessionDate = asString(row.sessionDate);
  const startTime = asString(row.startTime);
  return {
    ...(row as Partial<CoachingSessionDto>),
    id: asString(row.id) ?? "",
    cohort_id: asString(row.cohort_id ?? row.cohortId),
    cohort_name: asString(row.cohort_name ?? row.cohortName),
    company_id: asString(row.company_id ?? row.companyId),
    title: asString(row.title),
    starts_at: asString(row.starts_at) ?? (sessionDate ? (startTime ? `${sessionDate}T${startTime}` : sessionDate) : undefined),
    timezone: asString(row.timezone),
    invite_status: asString(row.invite_status ?? row.inviteStatus),
    coach_id: asString(row.coach_id ?? row.coachUserId),
    coach_name: asString(row.coach_name ?? row.coachName),
    status: asString(row.status)
  };
};

function asStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const items = value.map((entry) => asString(entry)).filter((entry): entry is string => Boolean(entry));
  return items.length ? items : undefined;
}

export const liveArtifactRow: LiveRowMapper<CoachingArtifactDto> = (row) => ({
  ...(row as Partial<CoachingArtifactDto>),
  id: asString(row.id) ?? "",
  session_id: asString(row.session_id ?? row.sessionId),
  title: asString(row.title),
  status: asString(row.status),
  // G6: carry the report taxonomy/visibility/body the backend already returns
  // (CoachingReportSummary) instead of dropping them.
  report_type: asString(row.report_type ?? row.reportType) as CoachingReportType | undefined,
  artifact_visibility: asString(row.artifact_visibility ?? row.artifactVisibility) as CoachingArtifactVisibility | undefined,
  delivery_mode: asString(row.delivery_mode ?? row.deliveryMode) as CoachingDeliveryMode | undefined,
  summary: asString(row.summary),
  highlights: asStringArray(row.highlights),
  action_items: asStringArray(row.action_items ?? row.actionItems),
  report_html: asString(row.report_html ?? row.reportHtml),
  cohort_name: asString(row.cohort_name ?? row.cohortName),
  session_title: asString(row.session_title ?? row.sessionTitle),
  reviewed_at: asString(row.reviewed_at ?? row.reviewedAt) ?? null,
  reviewed_by: asString(row.reviewed_by ?? row.reviewedBy) ?? null,
  approved_at: asString(row.approved_at ?? row.approvedAt) ?? null,
  download_policy: asString(row.download_policy ?? row.downloadPolicy)
});

/**
 * Analytics rollup envelope unwrappers (analytics spec §4.4). The live backend
 * wraps each rollup as `{ contract_version, <family>, <family>Evidence }`; mock
 * mode already answers in the inner shape. Each treats zero-filled as the empty
 * state (never an error).
 */
function unwrapAnalyticsFamily<T>(payload: unknown, family: string): T {
  if (isRecord(payload) && isRecord(payload[family])) return payload[family] as T;
  return payload as T;
}

export const liveOrgAnalyticsSummary: LiveRowMapper<CoachingOrgAnalyticsSummary> = (row) =>
  unwrapAnalyticsFamily<CoachingOrgAnalyticsSummary>(row, "analytics");

export const liveOrgAnalyticsTimeSeries: LiveRowMapper<CoachingOrgAnalyticsTimeSeries> = (row) =>
  unwrapAnalyticsFamily<CoachingOrgAnalyticsTimeSeries>(row, "timeSeries");

export const liveOrgReportStatus: LiveRowMapper<CoachingOrgReportStatus> = (row) =>
  unwrapAnalyticsFamily<CoachingOrgReportStatus>(row, "reportStatus");

export const liveResourceRow: LiveRowMapper<CoachingResourceDto> = (row) => ({
  ...(row as Partial<CoachingResourceDto>),
  id: asString(row.id) ?? "",
  title: asString(row.title ?? row.name) ?? "",
  status: asString(row.status),
  upload_id: asString(row.upload_id ?? row.uploadId),
  download_status: asString(row.download_status ?? row.downloadStatus),
  // Wave 8: carry the live resource fields the categories + manage surfaces read.
  resource_type: asString(row.resource_type ?? row.resourceType),
  description: asString(row.description),
  url: asString(row.url),
  is_public: typeof (row.is_public ?? row.isPublic) === "boolean" ? Boolean(row.is_public ?? row.isPublic) : undefined,
  company_name: asString(row.company_name ?? row.companyName),
  cohort_name: asString(row.cohort_name ?? row.cohortName)
});

export const liveHistoryEventRow: LiveRowMapper<CoachingHistoryEventDto> = (row) => ({
  ...(row as Partial<CoachingHistoryEventDto>),
  id: asString(row.id) ?? "",
  occurred_at: asString(row.occurred_at ?? row.createdAt),
  actor_id: asString(row.actor_id ?? row.actorId),
  action: asString(row.action),
  summary: asString(row.summary ?? row.detail),
  // Wave 10: the event-history projection metadata used to derive the distinct
  // log VIEWS (category-filtered) over the one real /event-history read.
  entity_type: asString(row.entity_type ?? row.entityType),
  entity_id: asString(row.entity_id ?? row.entityId),
  category: asString(row.category),
  actor: asString(row.actor ?? row.actor_id ?? row.actorId),
  changed_fields: Array.isArray(row.changed_fields)
    ? (row.changed_fields as string[])
    : Array.isArray(row.changedFields)
      ? (row.changedFields as string[])
      : undefined
});

export const liveUserRow: LiveRowMapper<CoachingUserDto> = (row) => {
  const role = asString(row.role);
  const canonicalRole = role === 'super_admin' || role === 'admin' || role === 'coach'
    || role === 'company_admin' || role === 'cohort_lead' || role === 'participant'
    ? role
    : undefined;
  const ypoCompanyIds = isRecord(row.ypo) && Array.isArray(row.ypo.companyIds) ? row.ypo.companyIds : undefined;
  const legacyCompanyIds = Array.isArray(row.companyIds) ? row.companyIds : undefined;
  const leadCohortIds = Array.isArray(row.leadCohortIds)
    ? row.leadCohortIds.map((id) => Number(id)).filter((id) => Number.isFinite(id))
    : [];
  return {
    ...(row as Partial<CoachingUserDto>),
    id: asString(row.id) ?? '',
    email: asString(row.email),
    name: asString(row.name) ?? [asString(row.firstName), asString(row.lastName)].filter(Boolean).join(' '),
    role: canonicalRole,
    status: asString(row.status),
    membershipStatus: asString(row.membershipStatus ?? row.membership_status),
    company_id: asString(row.company_id ?? legacyCompanyIds?.[0] ?? ypoCompanyIds?.[0]),
    cohort_ids: leadCohortIds.map(String),
    leadCohortIds
  };
};
